import { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — react-icons + React 19 typings
import {
  FaClipboardList as FaClipboardListRaw,
  FaFileAlt as FaFileAltRaw,
  FaFilm as FaFilmRaw,
  FaImage as FaImageRaw,
  FaMousePointer as FaMousePointerRaw,
  FaParagraph as FaParagraphRaw,
  FaQuestion as FaQuestionRaw,
} from 'react-icons/fa';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaClipboardList: any = FaClipboardListRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaFilm: any = FaFilmRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaParagraph: any = FaParagraphRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaImage: any = FaImageRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaFileAlt: any = FaFileAltRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaQuestion: any = FaQuestionRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaMousePointer: any = FaMousePointerRaw;

export type LessonContentBlockType = 'text' | 'video' | 'image' | 'file' | 'pdf_pages' | 'quiz' | 'assignment' | 'links';

export type LessonContentBlock = {
  id: string;
  type: LessonContentBlockType;
  title: string;
  meta: string;
  body: string;
  url?: string;
  assetUrl?: string;
  assetName?: string;
  quizId?: string;
  quizTitle?: string;
  assignmentId?: string;
  assignmentTitle?: string;
  links?: string[];
  textbookId?: string;
  textbookTitle?: string;
  pdfUrl?: string;
  pdfPageExpression?: string;
  pdfPages?: number[];
  textbookPageCount?: number;
};

type LessonQuizRef = {
  id: string;
  title: string;
  description?: string | null;
  due_at?: string | null;
  points?: number;
};

type LessonAssignmentRef = {
  id: string;
  title: string;
  due_at?: string | null;
  points?: number;
};

type QuizQuestion = {
  id: string;
  prompt: string;
  options: string[];
  answer: string;
  points: number;
};

const LESSON_CONTENT_VERSION = 1;
const WORK_CONTENT_VERSION = 1;

function parseLinksFromText(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function toExternalHref(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    return new URL(trimmed).toString();
  } catch {
    try {
      return new URL(`https://${trimmed}`).toString();
    } catch {
      return null;
    }
  }
}

function getYoutubeEmbedUrl(value: string | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) return null;
  try {
    const url = new URL(trimmed);
    const host = url.hostname.replace(/^www\./i, '').toLowerCase();
    let videoId = '';
    if (host === 'youtu.be') {
      videoId = url.pathname.slice(1);
    } else if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (url.pathname === '/watch') {
        videoId = url.searchParams.get('v') ?? '';
      } else if (url.pathname.startsWith('/shorts/')) {
        videoId = url.pathname.split('/')[2] ?? '';
      } else if (url.pathname.startsWith('/embed/')) {
        videoId = url.pathname.split('/')[2] ?? '';
      }
    }
    if (!videoId) return null;
    return `https://www.youtube.com/embed/${encodeURIComponent(videoId)}`;
  } catch {
    return null;
  }
}

function parseQuizQuestions(raw: string | null | undefined): QuizQuestion[] {
  if (!raw?.trim()) return [];
  try {
    const parsed = JSON.parse(raw) as { version?: number; questions?: unknown[] };
    if (parsed?.version !== WORK_CONTENT_VERSION || !Array.isArray(parsed.questions)) return [];
    return parsed.questions
      .map((item): QuizQuestion | null => {
        if (!item || typeof item !== 'object') return null;
        const row = item as Record<string, unknown>;
        const prompt = typeof row.prompt === 'string' ? row.prompt.trim() : '';
        if (!prompt) return null;
        return {
          id: typeof row.id === 'string' ? row.id : `${prompt}-${Math.random().toString(36).slice(2, 7)}`,
          prompt,
          options:
            Array.isArray(row.options) && row.options.every((value) => typeof value === 'string')
              ? (row.options as string[]).map((value) => value.trim()).filter(Boolean)
              : [],
          answer: typeof row.answer === 'string' ? row.answer.trim() : '',
          points: typeof row.points === 'number' && Number.isFinite(row.points) ? Math.max(1, row.points) : 1,
        };
      })
      .filter((question): question is QuizQuestion => Boolean(question));
  } catch {
    return [];
  }
}

export function parseLessonStoredDescription(
  raw: string | null | undefined
): { plainDescription: string; blocks: LessonContentBlock[] } {
  if (!raw?.trim()) return { plainDescription: '', blocks: [] };
  try {
    const parsed = JSON.parse(raw) as { version?: number; blocks?: unknown[]; plainDescription?: string };
    if (parsed && typeof parsed === 'object' && parsed.version === LESSON_CONTENT_VERSION && Array.isArray(parsed.blocks)) {
      return {
        plainDescription: typeof parsed.plainDescription === 'string' ? parsed.plainDescription : '',
        blocks: parsed.blocks
          .map((rawBlock): LessonContentBlock | null => {
            if (!rawBlock || typeof rawBlock !== 'object') return null;
            const block = rawBlock as Record<string, unknown>;
            if (typeof block.id !== 'string' || typeof block.type !== 'string') return null;
            const normalizedType: LessonContentBlockType =
              block.type === 'interactive'
                ? 'links'
                : block.type === 'links' ||
                    block.type === 'video' ||
                    block.type === 'image' ||
                    block.type === 'file' ||
                    block.type === 'pdf_pages' ||
                    block.type === 'quiz' ||
                    block.type === 'assignment'
                  ? block.type
                  : 'text';
            const normalized: LessonContentBlock = {
              id: block.id,
              type: normalizedType,
              title: typeof block.title === 'string' ? block.title : '',
              meta: typeof block.meta === 'string' ? block.meta : '',
              body: typeof block.body === 'string' ? block.body : '',
              links:
                Array.isArray(block.links) && block.links.every((value) => typeof value === 'string')
                  ? (block.links as string[])
                  : normalizedType === 'links'
                    ? parseLinksFromText(typeof block.body === 'string' ? block.body : '')
                    : [],
            };
            if (typeof block.url === 'string') normalized.url = block.url;
            if (typeof block.assetUrl === 'string') normalized.assetUrl = block.assetUrl;
            if (typeof block.assetName === 'string') normalized.assetName = block.assetName;
            if (typeof block.quizId === 'string') normalized.quizId = block.quizId;
            if (typeof block.quizTitle === 'string') normalized.quizTitle = block.quizTitle;
            if (typeof block.assignmentId === 'string') normalized.assignmentId = block.assignmentId;
            if (typeof block.assignmentTitle === 'string') normalized.assignmentTitle = block.assignmentTitle;
            if (typeof block.textbookId === 'string') normalized.textbookId = block.textbookId;
            if (typeof block.textbookTitle === 'string') normalized.textbookTitle = block.textbookTitle;
            if (typeof block.pdfUrl === 'string') normalized.pdfUrl = block.pdfUrl;
            if (typeof block.pdfPageExpression === 'string') normalized.pdfPageExpression = block.pdfPageExpression;
            if (
              Array.isArray(block.pdfPages) &&
              block.pdfPages.every((value) => typeof value === 'number' && Number.isFinite(value))
            ) {
              normalized.pdfPages = (block.pdfPages as number[]).map((value) => Math.max(1, Math.floor(value)));
            }
            if (typeof block.textbookPageCount === 'number' && Number.isFinite(block.textbookPageCount)) {
              normalized.textbookPageCount = Math.max(1, Math.floor(block.textbookPageCount));
            }
            return normalized;
          })
          .filter((block): block is LessonContentBlock => Boolean(block)),
      };
    }
  } catch {
    /* legacy plain-text description */
  }
  return { plainDescription: raw, blocks: [] };
}

export function lessonBlockAccent(type: LessonContentBlockType): 'blue' | 'orange' | 'purple' | 'gray' {
  if (type === 'video') return 'blue';
  if (type === 'pdf_pages') return 'blue';
  if (type === 'text') return 'orange';
  if (type === 'quiz' || type === 'assignment') return 'purple';
  return 'gray';
}

export function lessonBlockIcon(type: LessonContentBlockType) {
  switch (type) {
    case 'video':
      return FaFilm;
    case 'text':
      return FaParagraph;
    case 'image':
      return FaImage;
    case 'file':
      return FaFileAlt;
    case 'pdf_pages':
      return FaFileAlt;
    case 'quiz':
      return FaQuestion;
    case 'assignment':
      return FaClipboardList;
    case 'links':
      return FaMousePointer;
    default:
      return FaParagraph;
  }
}

function blockTypeLabel(type: LessonContentBlockType): string {
  if (type === 'video') return 'Video block';
  if (type === 'text') return 'Text block';
  if (type === 'image') return 'Image block';
  if (type === 'file') return 'File block';
  if (type === 'pdf_pages') return 'PDF page block';
  if (type === 'quiz') return 'Quiz block';
  if (type === 'assignment') return 'Assignment block';
  return 'Links block';
}

const BlockListWrap = styled.div`
  display: grid;
  gap: 0.62rem;
`;

const BlockCard = styled.article<{ $accent: 'blue' | 'orange' | 'purple' | 'gray' }>`
  border-radius: 12px;
  border: 1px solid color-mix(in srgb, var(--outline) 82%, transparent);
  border-left: 3px solid
    ${({ $accent }) =>
      $accent === 'blue'
        ? '#3b82f6'
        : $accent === 'orange'
          ? '#f59e0b'
          : $accent === 'purple'
            ? '#8b5cf6'
            : '#64748b'};
  padding: 0.62rem 0.72rem 0.72rem;
  background: var(--surface);
`;

const BlockHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
`;

const BlockIcon = styled.span<{ $accent: 'blue' | 'orange' | 'purple' | 'gray' }>`
  width: 26px;
  height: 26px;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ $accent }) =>
    $accent === 'blue'
      ? '#1d4ed8'
      : $accent === 'orange'
        ? '#b45309'
        : $accent === 'purple'
          ? '#6d28d9'
          : '#334155'};
  background: ${({ $accent }) =>
    $accent === 'blue'
      ? 'rgba(59, 130, 246, 0.18)'
      : $accent === 'orange'
        ? 'rgba(245, 158, 11, 0.18)'
        : $accent === 'purple'
          ? 'rgba(139, 92, 246, 0.18)'
          : 'var(--surfaceVariant)'};
`;

const BlockTitle = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text);
`;

const BlockMeta = styled.div`
  margin-top: 0.08rem;
  font-size: 0.72rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const BlockBody = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.83rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  line-height: 1.45;
  white-space: pre-wrap;
`;

const VideoPreview = styled.div`
  margin-top: 0.55rem;
  border-radius: 8px;
  aspect-ratio: 16 / 9;
  border: 1px solid var(--outline);
  background: linear-gradient(145deg, #1e3a5f 0%, #f97316 45%, #fbbf24 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2c7be5;
`;

const VideoPlay = styled.span`
  width: 40px;
  height: 40px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
`;

const VideoEmbed = styled.iframe`
  margin-top: 0.55rem;
  width: 100%;
  aspect-ratio: 16 / 9;
  border: 1px solid var(--outline);
  border-radius: 8px;
  background: #000;
`;

const BlockImage = styled.img`
  margin-top: 0.55rem;
  width: 100%;
  max-height: 280px;
  object-fit: contain;
  border-radius: 8px;
  border: 1px solid var(--outline);
  background: var(--surfaceVariant);
`;

const ActionLink = styled.a`
  margin-top: 0.55rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 0 0.8rem;
  border-radius: 999px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--primary);
  text-decoration: none;
  font-size: 0.8rem;
  font-weight: 700;
  &:hover {
    background: var(--surfaceVariant);
  }
`;

const InternalActionLink = styled(Link)`
  margin-top: 0.55rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 0 0.8rem;
  border-radius: 999px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--primary);
  text-decoration: none;
  font-size: 0.8rem;
  font-weight: 700;
  &:hover {
    background: var(--surfaceVariant);
  }
`;

const InternalActionButton = styled.button`
  margin-top: 0.55rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 0 0.8rem;
  border-radius: 999px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--primary);
  text-decoration: none;
  font-size: 0.8rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  &:hover {
    background: var(--surfaceVariant);
  }
`;

const QuizList = styled.div`
  margin-top: 0.5rem;
  display: grid;
  gap: 0.6rem;
`;

const QuizQuestionCard = styled.div`
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.52rem 0.62rem;
  background: var(--surfaceVariant);
`;

const QuizQuestionTitle = styled.p`
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.35;
  color: var(--text);
  font-weight: 600;
`;

const QuizPoints = styled.span`
  margin-left: 0.4rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  font-size: 0.74rem;
  font-weight: 500;
`;

const QuizOptions = styled.ul`
  margin: 0.35rem 0 0;
  padding-left: 1.05rem;
  display: grid;
  gap: 0.25rem;
`;

const QuizOption = styled.li`
  color: var(--textSecondary, var(--onSurfaceVariant));
  font-size: 0.79rem;
  line-height: 1.35;
`;

const QuizAnswer = styled.p`
  margin: 0.38rem 0 0;
  color: var(--text);
  font-size: 0.8rem;
  line-height: 1.35;
`;

const QuizSummary = styled.p`
  margin: 0.5rem 0 0;
  color: var(--textSecondary, var(--onSurfaceVariant));
  font-size: 0.8rem;
  line-height: 1.35;
`;

const AssignmentCard = styled.div`
  margin-top: 0.5rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.62rem 0.7rem;
  background: color-mix(in srgb, #8b5cf6 10%, var(--surface));
`;

const AssignmentTitle = styled.p`
  margin: 0;
  font-size: 0.83rem;
  color: var(--text);
  font-weight: 700;
`;

const AssignmentMeta = styled.p`
  margin: 0.28rem 0 0;
  font-size: 0.76rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const GenericList = styled.ul`
  margin: 0.5rem 0 0;
  padding-left: 1.1rem;
  display: grid;
  gap: 0.35rem;
`;

const GenericListItem = styled.li`
  color: var(--textSecondary, var(--onSurfaceVariant));
  font-size: 0.82rem;
  line-height: 1.4;
`;

const GenericListLink = styled.a`
  color: var(--primary);
  text-decoration: underline;
  word-break: break-word;
`;

export function LessonBlockRenderer({
  blocks,
  quizzes = [],
  assignments = [],
  classId,
  showQuizAnswers = false,
}: {
  blocks: LessonContentBlock[];
  quizzes?: LessonQuizRef[];
  assignments?: LessonAssignmentRef[];
  classId?: string;
  showQuizAnswers?: boolean;
}) {
  const [openQuizBlockIds, setOpenQuizBlockIds] = useState<Set<string>>(new Set());

  const toggleQuizBlock = (blockId: string) => {
    setOpenQuizBlockIds((prev) => {
      const next = new Set(prev);
      if (next.has(blockId)) next.delete(blockId);
      else next.add(blockId);
      return next;
    });
  };

  return (
    <BlockListWrap>
      {blocks.map((block) => {
        const accent = lessonBlockAccent(block.type);
        const Icon = lessonBlockIcon(block.type);
        const trimmedBody = block.body.trim();
        const videoHref = toExternalHref(block.url ?? '');
        const videoEmbed = getYoutubeEmbedUrl(block.url);
        const imageHref = toExternalHref(block.assetUrl ?? '');
        const fileHref = toExternalHref(block.assetUrl ?? block.pdfUrl ?? '');
        const pdfHref = toExternalHref(block.pdfUrl ?? block.assetUrl ?? '');
        const pdfPages = (block.pdfPages ?? []).filter((value) => Number.isFinite(value) && value > 0);
        const links = (block.links ?? parseLinksFromText(block.body)).map((value) => ({
          label: value,
          href: toExternalHref(value),
        }));
        const linkedQuiz =
          quizzes.find((quiz) => quiz.id === block.quizId) ?? quizzes.find((quiz) => quiz.title === block.quizTitle) ?? null;
        const quizQuestions = parseQuizQuestions(linkedQuiz?.description ?? null);
        const linkedAssignment =
          assignments.find((assignment) => assignment.id === block.assignmentId) ??
          assignments.find((assignment) => assignment.title === block.assignmentTitle) ??
          null;
        const quizTarget = block.quizId
          ? `/classroom-teacher/quizzes/${encodeURIComponent(block.quizId)}/edit`
          : classId
            ? `/classroom-teacher/quizzes?classId=${encodeURIComponent(classId)}`
            : '/classroom-teacher/quizzes';
        const isQuizOpen = openQuizBlockIds.has(block.id);
        const assignmentTarget = block.assignmentId
          ? `/classroom-teacher/assignments/${encodeURIComponent(block.assignmentId)}`
          : classId
            ? `/classroom-teacher/assignments?classId=${encodeURIComponent(classId)}`
            : '/classroom-teacher/assignments';
        return (
          <BlockCard key={block.id} $accent={accent}>
            <BlockHead>
              <BlockIcon $accent={accent} aria-hidden>
                <Icon size={14} />
              </BlockIcon>
              <div>
                <BlockTitle>{block.title || 'Untitled block'}</BlockTitle>
                <BlockMeta>{block.meta || blockTypeLabel(block.type)}</BlockMeta>
              </div>
            </BlockHead>
            {block.type === 'video' ? (
              videoEmbed ? (
                <VideoEmbed
                  title={block.title || 'Lesson video'}
                  src={videoEmbed}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <VideoPreview aria-hidden>
                  <VideoPlay>▶</VideoPlay>
                </VideoPreview>
              )
            ) : null}
            {block.type === 'video' && videoHref ? (
              <ActionLink href={videoHref} target="_blank" rel="noopener noreferrer">
                Open video
              </ActionLink>
            ) : null}
            {block.type === 'image' && imageHref ? (
              <BlockImage src={imageHref} alt={block.title || block.assetName || 'Lesson image'} loading="lazy" />
            ) : null}
            {block.type === 'file' && fileHref ? (
              <ActionLink href={fileHref} download={block.assetName || true}>
                Download {block.assetName || 'file'}
              </ActionLink>
            ) : null}
            {block.type === 'pdf_pages' ? (
              pdfHref ? (
                <div style={{ display: 'grid', gap: '0.35rem' }}>
                  <ActionLink href={pdfHref} target="_blank" rel="noopener noreferrer">
                    Open textbook PDF
                  </ActionLink>
                  {pdfPages.length > 0 ? (
                    <BlockMeta>
                      Pages: {pdfPages.join(', ')}
                      {block.textbookTitle ? ` · ${block.textbookTitle}` : ''}
                    </BlockMeta>
                  ) : null}
                </div>
              ) : (
                <BlockMeta>No textbook PDF link found for this block.</BlockMeta>
              )
            ) : null}
            {block.type === 'quiz' ? (
              <InternalActionButton type="button" onClick={() => toggleQuizBlock(block.id)}>
                {isQuizOpen ? 'Hide quiz' : 'Open quiz'}
              </InternalActionButton>
            ) : null}
            {block.type === 'quiz' && isQuizOpen ? (
              quizQuestions.length > 0 ? (
                <QuizList>
                  {quizQuestions.map((question, index) => (
                    <QuizQuestionCard key={`${block.id}-${question.id}-${index}`}>
                      <QuizQuestionTitle>
                        Q{index + 1}. {question.prompt}
                        <QuizPoints>{question.points} pt</QuizPoints>
                      </QuizQuestionTitle>
                      {question.options.length > 0 ? (
                        <QuizOptions>
                          {question.options.map((option, optionIndex) => (
                            <QuizOption key={`${question.id}-opt-${optionIndex}`}>{option}</QuizOption>
                          ))}
                        </QuizOptions>
                      ) : null}
                      {showQuizAnswers && question.answer ? <QuizAnswer>Answer: {question.answer}</QuizAnswer> : null}
                    </QuizQuestionCard>
                  ))}
                </QuizList>
              ) : linkedQuiz ? (
                <QuizSummary>
                  {linkedQuiz.points ? `${linkedQuiz.points} pts` : 'No points set'}
                  {linkedQuiz.due_at
                    ? ` · Due ${new Date(linkedQuiz.due_at).toLocaleString()}`
                    : ' · No due date'}
                </QuizSummary>
              ) : (
                <QuizSummary>No linked quiz found for this block.</QuizSummary>
              )
            ) : null}
            {block.type === 'quiz' && isQuizOpen ? (
              <InternalActionLink to={quizTarget}>Edit quiz</InternalActionLink>
            ) : null}
            {block.type === 'assignment' ? (
              linkedAssignment ? (
                <AssignmentCard>
                  <AssignmentTitle>{linkedAssignment.title}</AssignmentTitle>
                  <AssignmentMeta>
                    {linkedAssignment.points ? `${linkedAssignment.points} pts` : 'No points set'}
                    {linkedAssignment.due_at
                      ? ` · Due ${new Date(linkedAssignment.due_at).toLocaleString()}`
                      : ' · No due date'}
                  </AssignmentMeta>
                </AssignmentCard>
              ) : block.assignmentTitle ? (
                <AssignmentCard>
                  <AssignmentTitle>{block.assignmentTitle}</AssignmentTitle>
                  <AssignmentMeta>Assignment link saved in this lesson.</AssignmentMeta>
                </AssignmentCard>
              ) : null
            ) : null}
            {block.type === 'assignment' ? (
              <InternalActionLink to={assignmentTarget}>Open assignment</InternalActionLink>
            ) : null}
            {block.type === 'links' && links.length > 0 ? (
              <GenericList>
                {links.map((entry, index) => (
                  <GenericListItem key={`${block.id}-link-${index}`}>
                    {entry.href ? (
                      <GenericListLink href={entry.href} target="_blank" rel="noopener noreferrer">
                        {entry.label}
                      </GenericListLink>
                    ) : (
                      entry.label
                    )}
                  </GenericListItem>
                ))}
              </GenericList>
            ) : null}
            {trimmedBody && block.type !== 'links' && block.type !== 'assignment' && block.type !== 'pdf_pages' ? (
              <BlockBody>{trimmedBody}</BlockBody>
            ) : null}
          </BlockCard>
        );
      })}
    </BlockListWrap>
  );
}
